import { exportOrg, exportHeaderWithSubheaders } from '../lib/export_org';
import { headerWithId } from '../lib/org_utils';
import { orgToMarkdown } from '../lib/org_to_markdown';
import {
  hasRemarkableSession,
  sendMarkdownToRemarkable,
  signInToRemarkable,
} from '../lib/remarkable_mcp';
import { currentRepositoryIdentity } from '../util/settings_persister';
import { setLoadingMessage, hideLoadingMessage, setDisappearingLoadingMessage } from './base';

const MESSAGE_MS = 4000;
const ERROR_MESSAGE_MS = 8000;

const todoKeywordsOf = (file) =>
  (file.get('todoKeywordSets') || []).flatMap((set) => set.get('keywords') || []).toJS();

// Org links and markup, reduced to what a document name on the tablet can show.
export const plainTitle = (rawTitle) =>
  (rawTitle || '')
    .replace(/\[\[[^\]]+\]\[([^\]]+)\]\]/g, '$1')
    .replace(/\[\[([^\]]+)\]\]/g, '$1')
    .replace(/(^|\s)[*/_=~+]([^\s].*?[^\s]|[^\s])[*/_=~+](?=\s|$)/g, '$1$2')
    .replace(/\s+/g, ' ')
    .trim();

const fileBaseName = (path) =>
  (path || 'organice').replace(/^.*\//, '').replace(/\.org(_archive)?$/i, '');

/**
 * The repository's short name for a document name: the last segment of a
 * GitLab project (`GitLab:org%2Fml` -> `ml`) or of a WebDAV endpoint,
 * `Dropbox` for Dropbox, nothing when signed out.
 */
export const repositoryShortName = (identity) => {
  if (!identity) return null;
  const [service, ...rest] = identity.split(':');
  const target = rest.join(':');
  if (service === 'GitLab' || service === 'WebDAV') {
    let decoded = target;
    try {
      decoded = decodeURIComponent(target);
    } catch (e) {
      // keep it as it is
    }
    const segments = decoded
      .replace(/^[a-z]+:\/\//i, '')
      .split('/')
      .filter(Boolean);
    return segments.length ? segments[segments.length - 1] : service;
  }
  return service || null;
};

const pad = (n) => String(n).padStart(2, '0');

/** Local date and time, `2026-10-31 14:05`. */
export const formatTimestamp = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
  `${pad(date.getHours())}:${pad(date.getMinutes())}`;

/**
 * `<file name> - <repository> - <date time>`; a single header is named after
 * its file and itself (`gtd: Write report - ml - 2026-10-31 14:05`).
 */
export const remarkableDocumentName = ({ fileName, headerTitle, repository, date }) =>
  [headerTitle ? `${fileName}: ${headerTitle}` : fileName, repository, formatTimestamp(date)]
    .filter(Boolean)
    .join(' - ');

// The heading inside the PDF: #+TITLE, else the file name.
const fileTitle = (path, linesBeforeHeadings) => {
  const titleLine = (linesBeforeHeadings || []).find((line) => /^\s*#\+TITLE:/i.test(line));
  if (titleLine) {
    const title = titleLine.replace(/^\s*#\+TITLE:\s*/i, '').trim();
    if (title) return title;
  }
  return fileBaseName(path);
};

/**
 * What to send: one header and everything below it (`headerId`), else the
 * narrowed subtree, else the whole file. `repository` and `now` default to
 * the signed-in repository and the current time.
 *
 * @returns {{markdown: string, documentName: string} | null}
 */
export const remarkableDocumentFromState = (
  state,
  { path, headerId, repository = repositoryShortName(currentRepositoryIdentity()), now } = {}
) => {
  const filePath = path || state.org.present.get('path');
  const file = state.org.present.getIn(['files', filePath]);
  if (!file || !file.get('headers')) return null;
  const headers = file.get('headers');
  const dontIndent = state.base.get('shouldNotIndentOnExport');
  const todoKeywords = todoKeywordsOf(file);
  const nameParts = { fileName: fileBaseName(filePath), repository, date: now || new Date() };

  const subtreeId = headerId || file.get('narrowedHeaderId');
  const header = subtreeId && headerWithId(headers, subtreeId);
  if (header) {
    const orgText = exportHeaderWithSubheaders(header, headers, {
      includeSubheaders: true,
      recursive: true,
      includeTitle: true,
      dontIndent,
    });
    return {
      markdown: orgToMarkdown(orgText, { todoKeywords }),
      documentName: remarkableDocumentName({
        ...nameParts,
        headerTitle: plainTitle(header.getIn(['titleLine', 'rawTitle'])),
      }),
    };
  }

  const linesBeforeHeadings = file.get('linesBeforeHeadings');
  const title = fileTitle(filePath, linesBeforeHeadings && linesBeforeHeadings.toJS());
  const orgText = exportOrg({ headers, linesBeforeHeadings, dontIndent });
  return {
    markdown: orgToMarkdown(orgText, { title, todoKeywords }),
    documentName: remarkableDocumentName(nameParts),
  };
};

/**
 * Send a page to the reMarkable. Dispatch it straight from a click handler:
 * when no sign-in exists yet the popup has to open before anything awaits.
 */
export const sendToRemarkable = (options = {}) => (dispatch, getState) => {
  const document = remarkableDocumentFromState(getState(), options);
  if (!document || !document.markdown.trim()) {
    dispatch(setDisappearingLoadingMessage('Nothing to send to the reMarkable.', MESSAGE_MS));
    return Promise.resolve(false);
  }

  const signIn = hasRemarkableSession() ? Promise.resolve() : signInToRemarkable();
  // Fire-and-forget callers (onClick) must not see an unhandled rejection.
  return signIn
    .then(() => {
      dispatch(setLoadingMessage(`Sending “${document.documentName}” to the reMarkable…`));
      return sendMarkdownToRemarkable(document);
    })
    .then(() => {
      dispatch(hideLoadingMessage());
      dispatch(
        setDisappearingLoadingMessage(
          `Sent “${document.documentName}” to the reMarkable.`,
          MESSAGE_MS
        )
      );
      return true;
    })
    .catch((error) => {
      dispatch(hideLoadingMessage());
      let message = error.message || 'Sending to the reMarkable failed.';
      if (error.kind === 'auth') message += ' Tap send again to sign in.';
      if (error.suggestion) message += ` ${error.suggestion}`;
      if (error.kind !== 'cancelled') console.error('reMarkable send failed:', error);
      dispatch(setDisappearingLoadingMessage(message, ERROR_MESSAGE_MS));
      return false;
    });
};
